import { Component, OnInit, EventEmitter, Output, Input } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ApplicationBusinessInfo } from '../application.business.info';
import { ConnectionService } from 'src/app/shared-services/connection.service';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/auth/auth.service';
import { distinctUntilChanged } from 'rxjs/operators';
import { GoogleAnalyticsService } from 'src/app/analytics/google.analytics.service';
import { ActivatedRoute } from '@angular/router';

@Component({
    selector: 'app-business-information',
    templateUrl: './business-information.component.html',
    styleUrls: ['./business-information.component.css', '../application.component.css']
})
export class BusinessInformationComponent implements OnInit {

    @Input() token: any;
    @Input() referralPage: any;
    @Input() submitted: any = false;
    @Input() industries: any[] = [];
    @Input() businessTypes: any[] = [];
    @Input() statesOfIncorporation: any[] = [];
    @Input() businessInfo: ApplicationBusinessInfo = new ApplicationBusinessInfo(this.formBuilder, this.authService);
    @Input() maxRequestedAmount: any = 0;

    @Output() continueClicked = new EventEmitter<ApplicationBusinessInfo>();
    @Output() soleContinueClicked = new EventEmitter<void>();

    cities: any[] = [];
    states: any[] = [];
    googleVariant: any;
    counties: any[] = [];
    googleExperience: any;
    userPOS: boolean = false;
    showForm: boolean = false;
    maxApplicationAmount: number = 0;
    production: boolean = environment.production;
    today = new Date();
    minDate = new Date("1900-1-1");
    zipValid = false;
    applicationStarted = false;

    constructor(
        private connectionService: ConnectionService,
        private formBuilder: FormBuilder,
        private authService: AuthService,
        private googleAnalyticsService: GoogleAnalyticsService,
        private route: ActivatedRoute
    ) {
        this.authService.maxApplicationAmount.then(
            (data: number) => {
                this.maxApplicationAmount = data;
            }
        );
    }

    ngOnInit() {
        this.avoidWhitespace();
        this.googleExperience = this.route.snapshot.queryParams.google_experience;
        this.googleVariant = this.route.snapshot.queryParams.google_variant;
    }

    bussinesTypeChange() {
        if (!this.applicationStarted) {
            this.applicationStarted = true;
            this.trackEvent('application-started');
        }

        this.trackEvent('business-type-selected', this.businessInfo.form.controls.businessType.value);

        if (this.businessInfo.form.controls.businessType.value == 'SoleProprietorship') {
            this.soleContinueClicked.emit();
        } else {
            this.showForm = true;
        }
    }

    onContinueClick() {
        this.submitted = true;

        if (this.businessInfo.form.invalid) {
            return;
        }

        this.trackEvent('business-information-completed');
        this.continueClicked.emit(this.businessInfo);
    }

    get bForm() { return this.businessInfo.form.controls; }

    onZipCodeKeyUp() {
        return new Promise<void>((resolve, reject) => {
            this.getZipCodeData(this.businessInfo.form.controls.zip.value).then(
                (data: any) => {
                    if (data.object) {
                        this.zipValid = false;
                        this.cities = data.object.city;
                        this.states = this.statesOfIncorporation; // data.object.state example: "UT"
                        this.counties = data.object.county;
                        this.businessInfo.form.controls.state.setValue(data.object.state);
                        this.businessInfo.form.controls.county.setValue(data.object.county[0]);
                        this.businessInfo.form.controls.city.setValue(data.object.city[0]);

                        resolve(data);
                    } else {
                        this.zipValid = true;
                        this.cities = [];
                        this.counties = [];
                        this.businessInfo.form.controls.county.setValue('');
                        this.businessInfo.form.controls.city.setValue('');
                        reject();
                    }
                },
                (error: any) => {
                    this.cities = [];
                    this.states = [];
                    this.businessInfo.form.controls.state.setValue('');
                    this.counties = [];
                    reject();
                }
            );
        });
    }

    onZipCodeKeyUpHandler() {
        this.onZipCodeKeyUp().then(
            () => { void (0); },
            () => { void (0); }
        );
    }

    getZipCodeData(zipCode: string) {
        return new Promise((resolve, reject) => {
            if (this.token) {
                this.connectionService.getRequestWithToken(`${environment.gateway}applicationNl/zipData/${zipCode}`, 'application/json', this.token).subscribe(
                    (data: any) => {
                        resolve(data);
                    },
                    (error: any) => {
                        reject(error);
                    }
                );
            } else {
                this.connectionService.getRequest(`${environment.gateway}application/zipData/${zipCode}`, 'application/json').subscribe(
                    (data: any) => {
                        resolve(data);
                    },
                    (error: any) => {
                        reject(error);
                    }
                );
            }
        });
    }

    onCompleteInformationClick() {
        this.businessInfo.setDefaultData();
        this.onZipCodeKeyUp().then(() => {
            this.businessInfo.setDefaultData();
        });
    }

    onBackClick() {
        this.trackEvent('back-to-business-type');
        this.businessInfo.form.controls.businessType.setValue('');
        this.submitted = false;
        this.showForm = false;
    }

    trackEvent(event: string, data?: any) {
        let customDimensions = {};
        customDimensions[environment.GA_DIMENSIONS.REFERRAL_PAGE] = this.referralPage;
        customDimensions[environment.GA_DIMENSIONS.GOOGLE_EXPERIENCE] = this.googleExperience ? this.googleExperience : undefined;
        customDimensions[environment.GA_DIMENSIONS.GOOGLE_VARIANT] = this.googleVariant ? this.googleVariant : undefined;
        if (data) { customDimensions[environment.GA_DIMENSIONS.DATA] = data; }
        this.googleAnalyticsService.trackEvent('application', event, this.token ? 'application-token' : 'application', 1, this.token ? customDimensions : null);
    }

    private avoidWhitespace() {
        this.bForm.legalCompanyName.valueChanges.pipe(distinctUntilChanged()).subscribe(res => {
            this.bForm.legalCompanyName.setValue((res).trimStart());
        });
        this.bForm.businessType.valueChanges.pipe(distinctUntilChanged()).subscribe(res => {
            this.bForm.businessType.setValue((res).trimStart());
        });
        this.bForm.doingBusinessAs.valueChanges.pipe(distinctUntilChanged()).subscribe(res => {
            this.bForm.doingBusinessAs.setValue((res).trimStart());
        });
        this.bForm.businessAddress.valueChanges.pipe(distinctUntilChanged()).subscribe(res => {
            this.bForm.businessAddress.setValue((res).trimStart());
        });
        this.bForm.zip.valueChanges.pipe(distinctUntilChanged()).subscribe(res => {
            this.bForm.zip.setValue((res).trimStart());
        });
        this.bForm.equipmentDescription.valueChanges.pipe(distinctUntilChanged()).subscribe(res => {
            this.bForm.equipmentDescription.setValue((res).trimStart());
        });
    }
}
